// @ts-ignore
import React, { ReactNode, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/components/wpl/button/src";
import { ContentBlock } from "@/components/wpl/content-block/src";
import Link from "@/components/wpl/link/src";
import { TextInput } from "@/components/wpl/text-input/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const mainCss = css({
    "paddingBlockStart": "4rem",
    "paddingBlockEnd": "6rem",
    ".content-block": {
        ".text-section": {
            paddingInlineStart: 0,
            paddingInlineEnd: 0,
            p: {
                fontFamily:
                    "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
                fontWeight: 300,
                fontSize: "1rem",
                marginBlockEnd: 0
            },
            h1: {
                fontSize: "4.8rem",
                fontFamily:
                    "'Charlie Display', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
                fontWeight: 500,
                color: "#253858"
            }
        }
    }
});
const searchMainContainerStyle = css({
    insetBlockStart: "100%",
    insetInlineStart: token("space.0"),
    lineHeight: token("space.300")
});
const searchFormStyle = css({
    lineHeight: token("space.300"),
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    label: {
        marginInlineStart: 0,
        fontWeight: 400,
        fontSize: "0.875rem",
        marginBlockEnd: token("space.050"),
        display: "block"
    },
    [media.above.sm]: {
        padding: "5px 20px 24px 20px"
    }
});
const searchContainerStyle = css({
    textAlign: "center",
    margin: `${token("space.0")} auto`,
    [media.above.md]: {
        paddingBlockStart: token("space.150")
    },
    [media.below.md]: {
        paddingBlockStart: 0
    }
});
const searchInputContainerStyle = css({
    display: "inline-block",
    marginInlineEnd: token("space.100"),
    textAlign: "left",
    [media.above.xs]: {
        "div label": {
            display: "none"
        },
        "width": "calc(50% - 91px)"
    },
    [media.only.xs]: {
        "div label": {
            display: "none"
        },
        "width": "calc(80% - 60px)"
    },
    [media.below.xs]: {
        "div label": {
            display: "none"
        },
        "width": "calc(100% - 60px)"
    }
});
const divStyle = css({
    [media.above.md]: {
        marginBlockStart: 0
    }
});
const searchIconButtonStyle = css({
    "backgroundColor": "#0052cc",
    "backgroundSize": token("space.200"),
    "border": "1px solid #0052cc",
    "borderRadius": token("space.050"),
    "cursor": "pointer",
    "verticalAlign": "bottom",
    "color": "#ffffff",
    "padding": `${token("space.0")} ${token("space.100")}`,
    "marginBlockEnd": token("space.075"),
    "svg": {
        color: "#ffffff"
    },
    "&:visited": {
        color: "#f4f5f7"
    },
    "&:hover, &:active": {
        backgroundColor: "#0049b0",
        border: "1px solid #0049b0"
    }
});

export const MainSection = () => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchText, setSearchText] = useState("");
    const sorry = intl.formatMessage(
        {
            id: "404.sorry",
            defaultMessage:
                "Sorry, the page you're looking for cannot be found{br}Visit our <HomepageLink>homepage</HomepageLink>, get <HelpLink>help</HelpLink>, or try searching"
        },
        {
            ...DEFAULT_VALUES,
            HomepageLink: (text: ReactNode) => (
                <Link
                    text={text as unknown as string}
                    aria-label={text as unknown as string}
                    href={useLocalizedHref("/")}
                    type={"inline"}
                />
            ),
            HelpLink: (text: ReactNode) => (
                <Link
                    text={text as unknown as string}
                    aria-label={text as unknown as string}
                    href={useLocalizedHref("/resources/support")}
                    type={"inline"}
                />
            )
        }
    );
    const search = intl.formatMessage({
        id: "404.search",
        defaultMessage: "Search"
    });

    return (
        <div css={mainCss}>
            <Row>
                <ContentBlock
                    isCentered={true}
                    cardStyle={true}
                    headerText={"404"}
                    headerType={"h1"}
                    bodyText={sorry as string}
                />
            </Row>
            <div css={divStyle}>
                <Row layout="8">
                    <div
                        css={searchMainContainerStyle}
                        id="wac-search"
                        data-testid="search-container"
                    >
                        <form
                            css={searchFormStyle}
                            action={useLocalizedHref(`/search`)}
                            method="GET"
                            id="wac-search-form"
                            data-testid="search-form"
                        >
                            <div
                                id="wac-search-container"
                                css={searchContainerStyle}
                            >
                                <div
                                    css={searchInputContainerStyle}
                                    id="wac-search-input-container"
                                >
                                    <TextInput
                                        ref={inputRef}
                                        className="fullWidth"
                                        placeholder={""}
                                        label={""}
                                        name="q"
                                        id="search-input"
                                        value={searchText}
                                        onChangeHandler={setSearchText}
                                        data-testid="search-input"
                                        aria-label="Search Input"
                                    />
                                </div>
                                <Button
                                    text={search}
                                    type={"primary"}
                                    size={"small"}
                                    css={searchIconButtonStyle}
                                />
                            </div>
                        </form>
                    </div>
                </Row>
            </div>
        </div>
    );
};
